import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "next/navigation";
import { transformUnit, UnitsResponse } from "../lib/useFetchUnits";
import { PropertyType } from "src/types";
import { ViewProps } from "../components/templates/design1/ListingPage1";
import { fetchUnits } from "../lib/useFetchUnits";

export const useUnits = ({
  isDemo,
  placeholder,
  type,
  properties,
  location,
}: {
  isDemo: boolean;
  placeholder: UnitsResponse;
  properties: PropertyType[];
  type?: ViewProps;
  location?: string;
}) => {
  const queryClient = useQueryClient();
  const [view, setView] = useState<"list" | "grid">("grid");
  const [hydrated, setHydrated] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | null>("newest");

  const searchParams = useSearchParams();

  const filters = useMemo(() => {
    const newFilters: Record<string, string> = {};
    for (const [key, value] of searchParams.entries()) {
      newFilters[key] = value;
    }
    return newFilters;
  }, [searchParams.toString()]);

  const hasRelevantParams = useMemo(
    () =>
      Object.entries(filters).some(
        ([key, value]) => key !== "sort" && key !== "sort_by" && value.trim() !== ""
      ),
    [filters]
  );

  useEffect(() => {
    setHydrated(true);
  }, []);

  // ✅ Only use initialData when there are NO filters (initial page load)
  const hasFilters = Object.keys(filters).length > 0;
  
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
 
    error,
  } = useInfiniteQuery({
    queryKey: ["units", type, filters, location],
    queryFn: async ({ pageParam }) => {
      return fetchUnits(
        {
          page: pageParam,
          sort_by: filters.sort_by || selectedValue || "newest",
          limit: 10,
          ...filters,
        },
        location
      );
    },
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.pagination;
      if (!pagination) return undefined; // stop pagination if missing
    
      const { current_page, total_pages } = pagination;
    
      return current_page < total_pages ? current_page + 1 : undefined;
    },
    
    initialPageParam: 1,
    enabled: !isDemo && type === "rent",
    staleTime: 1000 * 60 * 5,
    retry: 2,
    // ✅ Only use placeholder data on initial load (no filters applied)
    initialData: !hasFilters && placeholder?.units?.length > 0 ? {
      pages: [placeholder],
      pageParams: [1],
    } : undefined,
    // ✅ Force refetch when filters change
    refetchOnMount: hasFilters ? "always" : false,
  });

  // ✅ Prefetch next page using prefetchInfiniteQuery
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && data?.pages) {
      queryClient.prefetchInfiniteQuery({
        queryKey: ["units", type, filters, location],
        queryFn: async ({ pageParam }) => {
          return fetchUnits(
            {
              page: pageParam,
              sort_by: filters.sort_by || selectedValue || "newest",
              limit: 10,
              ...filters,
            },
            location
          );
        },
        initialPageParam: data.pages.length + 1,
      });
    }
  }, [hasNextPage, isFetchingNextPage, data?.pages, queryClient, type, filters, location, selectedValue]);

  const handleSelect = (value: string) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("sort_by", value);
    window.history.pushState({}, "", `?${searchParams.toString()}`);
    setSelectedValue(value);
  };

  const units = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => 
      page?.units?.map(transformUnit) ?? []
    );
  }, [data]);

  const unitss = useMemo(
    () => (isDemo ? [] : placeholder?.units?.map(transformUnit) ?? []),
    [isDemo, placeholder]
  );

  const dataToMap = isDemo ? properties : type === "rent" ? units : properties;
  
  const totalResults =
    isDemo || type !== "rent"
      ? properties.length
      : data?.pages?.[0]?.pagination?.total ?? 0;

  const displayedCount = Math.min(dataToMap.length, totalResults);

  return {
    displayedCount,
    setSelectedValue,
    hasMore: hasNextPage ?? false,
    isFetching: isFetchingNextPage,
    totalResults,
    dataToMap,
    handleLoadMore: fetchNextPage,
    view,
    hasRelevantParams,
    setView,
    loading: isLoading,
    error,
    handleSelect,
    unitss,
    units,
    hydrated,
    selectedValue,
  };
};